// lib/daimon/hosted.client.ts
import type { HostedDaimonBinding, HostedDaimonBridge, HostedDaimonContext, HostedDaimonResult, HostedDaimonTool } from './hosted';

/** A revoked lease invalidates queued work; completed app mutations are never described as undone. */
export function createHostedDaimonBridge(adapter: {
  context(): HostedDaimonContext;
  load(binding: HostedDaimonBinding): Promise<void>;
  interrupt(itemId?: string): void;
  userTurn(text: string, itemId: string): void;
  execute(tool: HostedDaimonTool): Promise<HostedDaimonResult>;
  revoke(): void;
}): HostedDaimonBridge {
  let binding: HostedDaimonBinding | null = null;
  let generation = 0;
  let ready = false;
  let queue: Promise<unknown> = Promise.resolve();
  const turns = new Set<string>();
  const results = new Map<string, Promise<HostedDaimonResult>>();
  const assertIdentity = (expected: HostedDaimonBinding) => {
    const current = adapter.context();
    if (current.isImpersonating !== Boolean(expected.impersonating) || current.dbUserId !== expected.dbUserId || current.userOrgId !== expected.userOrgId) {
      binding = null; generation += 1; adapter.revoke();
      throw new Error('The hosted Fater account does not match this call.');
    }
  };
  const assertCall = (callId: string, version = generation) => {
    if (!ready || !binding || binding.callId !== callId || version !== generation) throw new Error('This hosted call has ended or changed.');
    assertIdentity(binding);
  };
  return {
    context: adapter.context,
    async bind(next) {
      if (binding) throw new Error('A hosted call is already bound.');
      if (!next.callId || !next.conversationId || !next.dbUserId || !next.userOrgId) throw new Error('The call binding is incomplete.');
      assertIdentity(next);
      const version = ++generation;
      binding = next; ready = false;
      try { await adapter.load(next); if (!binding || binding.callId !== next.callId || version !== generation) throw new Error('This hosted call has ended or changed.'); ready = true; assertCall(next.callId, version); }
      catch (error) { if (version === generation) { binding = null; generation += 1; adapter.revoke(); } throw error; }
      turns.clear(); results.clear();
      return adapter.context();
    },
    async interrupt(callId, itemId) {
      assertCall(callId);
      adapter.interrupt(itemId);
    },
    async userTurn(turn) {
      assertCall(turn.callId);
      if (!turn.itemId) throw new Error('The user turn has no identity.');
      if (!turns.has(turn.itemId)) { turns.add(turn.itemId); adapter.userTurn(turn.text, turn.itemId); }
      return adapter.context();
    },
    execute(tool) {
      try { assertCall(tool.callId); } catch (error) { return Promise.reject(error); }
      if (!tool.toolCallId) return Promise.reject(new Error('The tool call has no identity.'));
      const prior = results.get(tool.toolCallId);
      if (prior) return prior;
      const version = generation;
      const result = queue.then(async () => {
        assertCall(tool.callId, version);
        const completed = await adapter.execute(tool);
        assertCall(tool.callId, version);
        return completed;
      });
      // Keep failures too: losing a result must never turn into executing the same mutation again.
      results.set(tool.toolCallId, result);
      queue = result.catch(() => undefined);
      return result;
    },
    async end(callId) {
      if (!binding || binding.callId !== callId) return;
      binding = null; ready = false; generation += 1;
      adapter.revoke();
    },
  };
}

/** Only an unambiguous answer approves the prepared operation, never an unrelated next turn. */
export function daimonApprovalAnswer(text: string): 'allow' | 'deny' | null {
  const answer = text.trim().toLowerCase().replace(/[.!?,;]+/g, '').replace(/\s+/g, ' ');
  if (/^(yes|yes please|yes go ahead|go ahead|confirm|confirmed|approve|approved|allow|do it|ok|okay|oui|oui vas-y|vas-y|confirme|je confirme|d'accord)$/.test(answer)) return 'allow';
  if (/^(no|no thanks|deny|cancel|stop|don't|do not|non|annule|annuler|pas maintenant)$/.test(answer)) return 'deny';
  return null;
}
